// Full-screen modal form for creating a custom HSK-mode character. Submits
// a CustomPersonaInput, which the parent turns into a Persona via
// buildCustomPersona() before adding to state and opening chat.

import { useState } from 'react';
import { ArrowLeft, Check } from 'lucide-react';
import type { CustomPersonaInput, HskLevel } from '@/lib/types';
import { HSK_RULES, VOICE_TONES } from '@/lib/hsk';
import { C } from '@/styles/tokens';

interface HSKBuilderProps {
  onCreate: (input: CustomPersonaInput) => void;
  onClose: () => void;
  /** True while the parent is awaiting the AI-generated greeting. Locks the
   *  form and shows a busy state on the submit button. */
  creating?: boolean;
}

const LEVELS: HskLevel[] = [1, 2, 3, 4, 5];
const PORTRAIT_CHOICES = ['custom-1', 'custom-2', 'custom-3', 'custom-4', 'custom-5', 'custom-6'] as const;

export function HSKBuilder({ onCreate, onClose, creating = false }: HSKBuilderProps) {
  const [hskLevel, setHskLevel] = useState<HskLevel>(3);
  const [name, setName] = useState('');
  const [age, setAge] = useState('');
  const [profession, setProfession] = useState('');
  const [interests, setInterests] = useState('');
  const [scenario, setScenario] = useState('');
  const [tone, setTone] = useState<string>('warm');
  const [customPortraitId, setCustomPortraitId] = useState<string>('');

  const isValid =
    name.trim().length > 0 &&
    age.trim().length > 0 &&
    profession.trim().length > 0 &&
    customPortraitId.length > 0;

  function handleSubmit() {
    if (!isValid || creating) return;
    onCreate({
      name: name.trim(),
      age: age.trim(),
      profession: profession.trim(),
      interests: interests.trim(),
      scenario: scenario.trim(),
      tone,
      hskLevel,
      customPortraitId,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col">
      <div
        className="absolute inset-0 anim-backdrop-in"
        style={{ background: 'rgba(26,22,20,0.45)' }}
        aria-hidden
        onClick={creating ? undefined : onClose}
      />
      <div
        role="dialog"
        aria-modal
        aria-label="New character"
        className="relative mt-auto flex-1 flex flex-col rounded-t-[28px] overflow-hidden anim-sheet-up"
        style={{ background: C.paper, boxShadow: '0 -8px 24px rgba(26,22,20,0.18)' }}
      >
        <div
          aria-hidden
          className="absolute top-2 left-1/2 w-10 h-1 rounded-full"
          style={{ transform: 'translateX(-50%)', background: 'rgba(26,22,20,0.18)' }}
        />
      <header
        className="pt-7 pb-3 px-5 flex items-center justify-between"
        style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={creating}
          aria-label="Close"
          className="w-9 h-9 rounded-full flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: 'white' }}
        >
          <ArrowLeft size={16} aria-hidden />
        </button>
        <div className="text-center">
          <div className="ff-display italic text-xl leading-tight inline-flex items-baseline gap-2" style={{ color: C.ink }}>
            New character
            <span className="ff-zh-serif not-italic text-sm opacity-50">· 新角色</span>
          </div>
          <div className="ff-body text-[9px] uppercase tracking-[0.3em] mt-0.5" style={{ color: C.inkLight }}>
            Customize Mode · 自定义
          </div>
        </div>
        <div className="w-9" aria-hidden />
      </header>

      <div className="flex-1 overflow-y-auto px-5 py-4">
        <div className="mb-5">
          <div className="ff-body text-[10px] uppercase tracking-[0.25em] mb-2" style={{ color: C.inkLight }}>
            HSK level
          </div>
          <div className="grid grid-cols-5 gap-1.5" role="radiogroup" aria-label="HSK level">
            {LEVELS.map((lvl) => {
              const active = hskLevel === lvl;
              return (
                <button
                  key={lvl}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setHskLevel(lvl)}
                  className="py-2 rounded-xl ff-body text-[12px] font-bold tracking-wide transition-all"
                  style={{
                    background: active ? C.ink : 'white',
                    color: active ? 'white' : C.ink,
                    border: `1px solid ${active ? C.ink : 'rgba(0,0,0,0.08)'}`,
                  }}
                >
                  {lvl}
                </button>
              );
            })}
          </div>
          <div className="ff-body text-[10px] mt-1.5" style={{ color: C.inkLight }}>
            {HSK_RULES[hskLevel].label}
          </div>
        </div>

        <div className="mb-5">
          <div className="ff-body text-[10px] uppercase tracking-[0.25em] mb-2" style={{ color: C.inkLight }}>
            Portrait <span style={{ color: C.vermillion }}>*</span>
          </div>
          <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Portrait">
            {PORTRAIT_CHOICES.map((slug, idx) => {
              const active = customPortraitId === slug;
              const n = idx + 1;
              return (
                <button
                  key={slug}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  aria-label={`Portrait ${n}`}
                  onClick={() => setCustomPortraitId(slug)}
                  className="relative aspect-square rounded-2xl overflow-hidden transition-transform active:scale-[0.97]"
                  style={{
                    border: `2px solid ${active ? C.vermillion : 'rgba(0,0,0,0.08)'}`,
                    background: 'white',
                  }}
                >
                  <img
                    src={`/portraits/custom/${n}.jpg`}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                </button>
              );
            })}
          </div>
        </div>

        <FieldText label="Name" required value={name} onChange={setName} placeholder="e.g. 小张 or Lisa" />
        <FieldText label="Age" required type="number" value={age} onChange={setAge} placeholder="e.g. 28" />
        <FieldText label="Profession" required value={profession} onChange={setProfession} placeholder="e.g. coffee shop owner, doctor, college student" />
        <FieldText label="Interests" optional value={interests} onChange={setInterests} placeholder="e.g. cycling, K-pop, cooking" />

        <div className="mb-5">
          <div className="ff-body text-[10px] uppercase tracking-[0.25em] mb-2" style={{ color: C.inkLight }}>
            Scenario / topic <span style={{ color: C.inkSofter, fontSize: 9 }}>(optional)</span>
          </div>
          <textarea
            value={scenario}
            onChange={(e) => setScenario(e.target.value)}
            placeholder="What do you want to practice? e.g. ordering at a restaurant, asking for directions, talking about your weekend"
            className="w-full px-3.5 py-3 rounded-2xl border ff-body text-[13px] leading-relaxed resize-none outline-none transition-all"
            rows={3}
            style={{
              background: 'white',
              borderColor: scenario ? C.vermillion : 'rgba(0,0,0,0.08)',
              color: C.ink,
              minHeight: 80,
            }}
            aria-label="Scenario or topic"
          />
        </div>

        <div className="mb-6">
          <div className="ff-body text-[10px] uppercase tracking-[0.25em] mb-2" style={{ color: C.inkLight }}>
            Voice tone
          </div>
          <div className="flex flex-col gap-1.5" role="radiogroup" aria-label="Voice tone">
            {VOICE_TONES.map((t) => {
              const active = tone === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setTone(t.id)}
                  className="text-left px-3.5 py-2.5 rounded-2xl transition-all"
                  style={{
                    background: active ? C.blush : 'white',
                    border: `1px solid ${active ? C.vermillion : 'rgba(0,0,0,0.08)'}`,
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="ff-body font-semibold text-[12px]" style={{ color: C.ink }}>
                      {t.label}
                    </div>
                    {active && <Check size={12} aria-hidden style={{ color: C.vermillion }} />}
                  </div>
                  <div className="ff-body text-[10px] mt-0.5 leading-relaxed" style={{ color: C.inkLight }}>
                    {t.desc}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div
        className="px-5 pb-7 pt-3 safe-bottom"
        style={{ borderTop: '1px solid rgba(0,0,0,0.06)', background: 'white' }}
      >
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!isValid || creating}
          aria-busy={creating}
          className="w-full py-3 rounded-full ff-body text-[12px] font-bold uppercase tracking-[0.2em] transition-transform active:scale-[0.98] disabled:cursor-not-allowed"
          style={{
            background: isValid && !creating ? C.ink : C.inkSofter,
            color: 'white',
          }}
        >
          {creating ? 'Creating…' : 'Create & start chatting →'}
        </button>
        {creating ? (
          <div className="ff-body text-[10px] text-center mt-2" style={{ color: C.inkLight }}>
            Writing their first message…
          </div>
        ) : !isValid ? (
          <div className="ff-body text-[10px] text-center mt-2" style={{ color: C.inkLight }}>
            Name, age, profession, and a portrait required
          </div>
        ) : null}
      </div>
      </div>
    </div>
  );
}

interface FieldTextProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: 'text' | 'number';
  required?: boolean;
  optional?: boolean;
}

function FieldText({ label, value, onChange, placeholder, type = 'text', required, optional }: FieldTextProps) {
  return (
    <div className="mb-4">
      <div className="ff-body text-[10px] uppercase tracking-[0.25em] mb-2" style={{ color: C.inkLight }}>
        {label}{' '}
        {required && <span style={{ color: C.vermillion }}>*</span>}
        {optional && <span style={{ color: C.inkSofter, fontSize: 9 }}>(optional)</span>}
      </div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3.5 py-3 rounded-2xl border ff-body text-[14px] outline-none transition-all"
        style={{
          background: 'white',
          borderColor: value ? C.vermillion : 'rgba(0,0,0,0.08)',
          color: C.ink,
        }}
        aria-label={label}
      />
    </div>
  );
}
